import { ComponentProps, FC, ReactElement, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertsCounter } from "shared/components/dataDisplay/AlertsCounter";
import { Menu, MenuContent, MenuTrigger, Text } from "shared/components/Spor";
import styled, { css, useTheme } from "styled-components";

type DropdownProps = {
  children?: ReactNode;
  label?: string;
  selectedAmount?: number | string;
  icon?: ReactElement | null;
  width?: string;
  height?: string;
  positioning?: ComponentProps<typeof Menu>["positioning"];
} & Omit<
  ComponentProps<typeof MenuTrigger>,
  | "children"
  | "icon"
  | "size"
  | "backgroundColor"
  | "height"
  | "fontSize"
  | "whiteSpace"
  | "maxWidth"
  | "width"
  | "outline"
>;

const StyledMenuTrigger = styled(MenuTrigger)<{ $showSelectedAmount: boolean }>`
  ${({ $showSelectedAmount }) =>
    $showSelectedAmount &&
    css`
      span {
        margin-left: 0;
      }
    `}
  &&&:focus,
  &&&:focus-visible,
  &&&[data-focus],
  &&&[data-focus-visible] {
    border: none;
  }
`;

const StyledMenuContent = styled(MenuContent)`
  background: ${({ theme }) => theme.surfaceDefault};
  box-shadow: ${({ theme }) => theme.boxShadow};
  border-radius: 12px;
`;

export const Dropdown: FC<DropdownProps> = ({
  children,
  label,
  selectedAmount,
  icon = null,
  width = undefined,
  height = "36px",
  positioning = undefined,
  ...triggerProps
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const theme = useTheme();
  let selectedAmountContent: ReactNode = null;

  const showSelectedAmount =
    typeof selectedAmount === "number" && selectedAmount > 0;
  if (showSelectedAmount) {
    selectedAmountContent = (
      <AlertsCounter
        alerts={Number(selectedAmount)}
        semantics="transparent"
        ml="auto"
      />
    );
  } else if (typeof selectedAmount === "string") {
    selectedAmountContent = <Text ml="auto">{selectedAmount}</Text>;
  }

  return (
    <Menu
      open={isOpen}
      onOpenChange={(details) => setIsOpen(details.open)}
      positioning={positioning}
    >
      <StyledMenuTrigger
        size="sm"
        icon={icon ?? undefined}
        backgroundColor={isOpen ? theme.colorSelected : undefined}
        height={height}
        fontSize="18px"
        whiteSpace="nowrap"
        maxWidth="100%"
        width={width ?? "fit-content"}
        outline={isOpen ? `2px solid ${theme.colorOutlineFocus}` : undefined}
        $showSelectedAmount={showSelectedAmount}
        {...triggerProps}
      >
        {label ?? t("Filter")}
        {selectedAmountContent}
      </StyledMenuTrigger>
      <StyledMenuContent>{children}</StyledMenuContent>
    </Menu>
  );
};
